import { TableRow, TableCell, IconButton, Box, Typography, useTheme, Chip, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { Product } from '../../../../../models/DB';
import { MoreVert, Edit, LocalOffer, Warning, CheckCircle, Cancel } from '@mui/icons-material';
import { Image, View } from 'react-native';
import { formatUpdatedTimestamp, calculateProfitMargin } from '../../../../../utils/customFunctions';
import { STOCK_LEVELS } from '../../../../../config/constants';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';
import { useCachedImage } from '../../../../../app/hooks/custom/useCachedImage';

interface ProductRowProps {
    product: Product;
    onEdit?: (product: Product) => void;
}

export const ProductRow = ({ product, onEdit }: ProductRowProps) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { imageSource } = useCachedImage(product.images['thumbnail']?.url);
    const profitMargin = calculateProfitMargin(product.price, product.costPrice);

    // Get thumbnail resize mode: per-image setting > product-level setting > default
    const thumbnailResizeMode = product.images?.['thumbnail']?.resizeMode 
        ?? product.thumbnailResizeMode 
        ?? 'contain';

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        onEdit?.(product);
        handleMenuClose();
    };

    return (
        <>
            <TableRow
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)'
                    }
                }}
            >
                <TableCell component="th" scope="product">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PermissionWrapper screen="products" permission="update">
                        <IconButton
                            size="small"
                                onClick={handleMenuClick}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover
                                    }
                                }}
                        >
                                <MoreVert fontSize="small" />
                        </IconButton>
                        </PermissionWrapper>
                        <View style={{
                            borderRadius: 4,
                            backgroundColor: "white",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden"
                        }}>
                        {imageSource && (
                            <Image
                                source={imageSource}
                                style={{
                                    minHeight: 30,
                                    maxHeight: 30,
                                    aspectRatio: 1 / 1,
                                }}
                                resizeMode={thumbnailResizeMode}
                            />
                        )}
                        </View>
                        <Typography variant="body2"
                            sx={{
                                fontSize: 11,
                                fontFamily: "primaryFont",
                                letterSpacing: 0.5,
                                textTransform: 'capitalize'
                            }}>
                            {product.name}
                        </Typography>
                    </Box>
                </TableCell>
                {/* Category - hidden on mobile */}
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>{product.category}</TableCell>
                
                {/* Mobile: Combined Stock & Status column */}
                <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                            icon={
                                product.stockOnHand === 0 ? (
                                    <Cancel sx={{ marginRight: 0.5 }} />
                                ) : product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD ? (
                                    <Warning sx={{ marginRight: 0.5 }} />
                                ) : (
                                    <CheckCircle sx={{ marginRight: 0.5 }} />
                                )
                            }
                            label={product.stockOnHand}
                            size="small"
                            color={
                                product.stockOnHand === 0
                                    ? 'error'
                                    : product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD
                                        ? 'warning'
                                        : 'success'
                            }
                            variant="outlined"
                            sx={{
                                fontSize: 10,
                                fontWeight: 600,
                                height: 20,
                                '& .MuiChip-icon': { fontSize: 12 },
                                '& .MuiChip-label': { px: 0.75 }
                            }}
                        />
                        <Chip
                            label={product.isActive !== false ? 'Active' : 'Inactive'}
                            size="small"
                            color={product.isActive !== false ? 'success' : 'default'}
                            variant={product.isActive !== false ? 'filled' : 'outlined'}
                            sx={{ fontSize: 9, fontWeight: 600, height: 18, '& .MuiChip-label': { px: 0.75 } }}
                        />
                    </Box>
                </TableCell>

                {/* Desktop: Status column */}
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                        icon={product.isActive !== false ? <CheckCircle /> : <Cancel />}
                        label={product.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        color={product.isActive !== false ? 'success' : 'default'}
                        variant={product.isActive !== false ? 'filled' : 'outlined'}
                        sx={{ fontSize: 11, fontWeight: 600 }}
                    />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" sx={{ fontSize: 12, fontFamily: "primaryFont" }}>
                            {config.currency}{product.price}
                        </Typography>
                        {profitMargin && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 10,
                                    color: theme.palette.success.main,
                                    fontWeight: 'bold'
                                }}
                            >
                                {profitMargin}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                    {product.costPrice ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="body2" sx={{ fontSize: 12, fontFamily: "primaryFont" }}>
                                {config.currency}{product.costPrice.toFixed(2)}
                            </Typography>
                        </Box>
                    ) : '-'}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {product.discount > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: 12,
                                    fontFamily: 'primaryFont',
                                    letterSpacing: 0.5,
                                    color: theme.palette.error.main,
                                    fontWeight: 'bold'
                                }}
                            >
                                {product.discountType === 'value' 
                                    ? `${config.currency}${product.discount} OFF`
                                    : `${product.discount}%`}
                            </Typography>
                            <LocalOffer sx={{ fontSize: 14, color: theme.palette.error.main }} />
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: 12,
                                fontFamily: 'primaryFont',
                                letterSpacing: 0.5,
                                color: theme.palette.text.secondary
                            }}
                        >
                            -
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Chip
                        icon={
                            product.stockOnHand === 0 ? (
                                <Cancel sx={{ marginRight: 1 }} />
                            ) : product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD ? (
                                <Cancel sx={{ marginRight: 1 }} />
                            ) : product.stockOnHand <= STOCK_LEVELS.MEDIUM_THRESHOLD ? (
                                <Warning sx={{ marginRight: 1 }} />
                            ) : (
                                            <CheckCircle sx={{ marginRight: 1 }} />
                            )
                        }
                        label={product.stockOnHand}
                        size="small"
                        color={
                            product.stockOnHand === 0
                                ? 'error'
                                : product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD
                                    ? 'error'
                                    : product.stockOnHand <= STOCK_LEVELS.MEDIUM_THRESHOLD
                                        ? 'warning'
                                        : 'success'
                        }
                        variant={product.stockOnHand > 0 && product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD ? 'filled' : 'outlined'}
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            minWidth: 70,
                            '& .MuiChip-icon': {
                                fontSize: 14
                            },
                            // Pulse animation only for LOW stock (not out of stock)
                            ...(product.stockOnHand >= 0 && product.stockOnHand <= STOCK_LEVELS.LOW_THRESHOLD && {
                                '@keyframes pulse': {
                                    '0%': {
                                        transform: 'scale(1)',
                                        boxShadow: `0 0 0 0 ${theme.palette.error.main}60`,
                                        backgroundColor: `${theme.palette.error.main}10`,
                                        borderColor: `${theme.palette.error.main}60`
                                    },
                                    '50%': {
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 0 0 6px ${theme.palette.error.main}00`,
                                        backgroundColor: `${theme.palette.error.main}40`,
                                        borderColor: `${theme.palette.error.main}40`
                                    },
                                    '100%': {
                                        transform: 'scale(1)',
                                        boxShadow: `0 0 0 0 ${theme.palette.error.main}60`,
                                        backgroundColor: `${theme.palette.error.main}10`,
                                        borderColor: `${theme.palette.error.main}60`
                                    }
                                },
                                animation: 'pulse 2s ease-in-out infinite'
                            })
                        }}
                    />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Chip
                        label={product.variantOptions?.length || 0}
                        size="small"
                        color={product.variantOptions?.length > 0 ? 'primary' : 'default'}
                        sx={{ fontSize: 11, minWidth: 40 }}
                    />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>{formatUpdatedTimestamp(product.updated)}</TableCell>
            </TableRow>

            {/* 3-Dot Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem' }}>
                    <Edit fontSize="small" sx={{ mr: 1 }} />
                    Edit Product
                </MenuItem>
            </Menu>
        </>
    );
};